import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Eye, EyeOff, Check, ExternalLink,
  Diamond, Crown, Medal, Plane, Star, Shield, Briefcase, GraduationCap, Heart, Globe2, BookUser, Home,
} from "lucide-react";
import { toast } from "sonner";
import portal from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const PACKAGE_ICONS = {
  diamond: Diamond, crown: Crown, medal: Medal, plane: Plane, star: Star, shield: Shield, briefcase: Briefcase,
  graduation: GraduationCap, heart: Heart, globe: Globe2, passport: BookUser, home: Home,
};

const TX = {
  en: {
    intro: "Packages are the offers shown on the website (Packages page and home page banner). Changes are live as soon as you save.",
    add: "New package", edit: "Edit package", editShort: "Edit", code: "Code (used in links and orders)", name: "Name", tagline: "Short description", badge: "Badge (optional, e.g. Best value)",
    features: "What's included", addFeature: "Add item", prices: "Prices", aoaHint: "AOA empty = converted from EUR at today's rate",
    icon: "Icon", cta: "Button", buy: "Buy online (card / bank transfer)", contact: "Contact sales (call / email)", apply: "Open a page (e.g. an application form)",
    link: "Page link", highlight: "Highlight this package", active: "Show on the website", save: "Save", cancel: "Cancel", saved: "Saved",
    hidden: "Hidden", live: "Live", remove: "Delete", removed: "Deleted", deactivated: "This package has orders, so it was hidden instead of deleted.",
    view: "View the Packages page", onRequest: "On request", confirm: "Delete this package?",
  },
  pt: {
    intro: "Os pacotes são as ofertas mostradas no site (página Pacotes e faixa da página inicial). As alterações ficam visíveis ao guardar.",
    add: "Novo pacote", edit: "Editar pacote", editShort: "Editar", code: "Código (usado em ligações e encomendas)", name: "Nome", tagline: "Descrição curta", badge: "Etiqueta (opcional, ex. Melhor valor)",
    features: "O que inclui", addFeature: "Adicionar item", prices: "Preços", aoaHint: "AOA vazio = convertido do EUR ao câmbio do dia",
    icon: "Ícone", cta: "Botão", buy: "Comprar online (cartão / transferência)", contact: "Contactar vendas (telefone / email)", apply: "Abrir uma página (ex. formulário)",
    link: "Ligação da página", highlight: "Destacar este pacote", active: "Mostrar no site", save: "Guardar", cancel: "Cancelar", saved: "Guardado",
    hidden: "Oculto", live: "Visível", remove: "Eliminar", removed: "Eliminado", deactivated: "Este pacote tem encomendas, por isso foi ocultado em vez de eliminado.",
    view: "Ver a página Pacotes", onRequest: "Sob consulta", confirm: "Eliminar este pacote?",
  },
  fr: {
    intro: "Les forfaits sont les offres affichées sur le site (page Forfaits et bandeau de l'accueil). Les changements sont visibles dès l'enregistrement.",
    add: "Nouveau forfait", edit: "Modifier le forfait", editShort: "Modifier", code: "Code (liens et commandes)", name: "Nom", tagline: "Description courte", badge: "Badge (facultatif, ex. Meilleur prix)",
    features: "Ce qui est inclus", addFeature: "Ajouter un élément", prices: "Prix", aoaHint: "AOA vide = converti depuis l'EUR au taux du jour",
    icon: "Icône", cta: "Bouton", buy: "Acheter en ligne (carte / virement)", contact: "Contacter les ventes (appel / email)", apply: "Ouvrir une page (ex. formulaire)",
    link: "Lien de la page", highlight: "Mettre ce forfait en avant", active: "Afficher sur le site", save: "Enregistrer", cancel: "Annuler", saved: "Enregistré",
    hidden: "Masqué", live: "En ligne", remove: "Supprimer", removed: "Supprimé", deactivated: "Ce forfait a des commandes : il a été masqué au lieu d'être supprimé.",
    view: "Voir la page Forfaits", onRequest: "Sur demande", confirm: "Supprimer ce forfait ?",
  },
};
const LANGS = ["pt", "en", "fr"];
const E3 = { en: "", pt: "", fr: "" };
const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");
const inp = "bg-slate-950 border-slate-700";

const Tri = ({ value = E3, onChange, multiline, testid }) => (
  <div className="grid gap-2 md:grid-cols-3">
    {LANGS.map((l) => (
      <div key={l}>
        <span className="text-[10px] font-bold uppercase text-slate-500">{l}</span>
        {multiline ? <Textarea rows={2} className={`mt-1 ${inp}`} value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
          : <Input className={`mt-1 ${inp}`} value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} data-testid={testid ? `${testid}-${l}` : undefined} />}
      </div>
    ))}
  </div>
);

const blank = () => ({ slug: "", name: { ...E3 }, tagline: { ...E3 }, badge: { ...E3 }, features: [{ ...E3 }], prices: { EUR: "", USD: "", AOA: "" },
  icon: "star", cta: "buy", link: "", highlight: false, active: true });

export const PackagesManager = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const tx = TX[lang] || TX.en;
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => portal.get("/settings/packages").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    const body = { ...edit, prices: Object.fromEntries(Object.entries(edit.prices || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined)) };
    try {
      if (edit.id) await portal.put(`/settings/packages/${edit.id}`, body); else await portal.post("/settings/packages", body);
      toast.success(tx.saved); setEdit(null); load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const toggle = async (p) => { try { await portal.put(`/settings/packages/${p.id}`, { active: !p.active }); load(); } catch (e) { err(e); } };
  const remove = async (p) => {
    if (!window.confirm(tx.confirm)) return;
    try { const r = await portal.delete(`/settings/packages/${p.id}`); toast.success(r.data.deleted ? tx.removed : tx.deactivated); load(); } catch (e) { err(e); }
  };
  const move = async (i, dir) => {
    const ids = rows.map((r) => r.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setRows(ids.map((id) => rows.find((r) => r.id === id)));
    try { await portal.post("/settings/packages/reorder", { ids }); } catch (e) { err(e); load(); }
  };

  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  const t3 = (v) => (v && (v[lang] || v.en || v.pt)) || "";
  return (
    <div className="space-y-4" data-testid="packages-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-400">{tx.intro}</p>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl border-slate-700" onClick={() => window.open("/packages", "_blank")}><ExternalLink className="h-4 w-4 mr-1.5" />{tx.view}</Button>
          <Button className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" onClick={() => setEdit(blank())} data-testid="package-add"><Plus className="h-4 w-4 mr-1.5" />{tx.add}</Button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((p, i) => {
          const Icon = PACKAGE_ICONS[p.icon] || Star;
          return (
            <div key={p.id} className={`relative rounded-2xl border bg-slate-900/40 p-5 ${p.highlight ? "border-venka-blue/50" : "border-slate-800"} ${p.active ? "" : "opacity-60"}`} data-testid={`package-row-${p.slug}`}>
              <div className="flex items-start gap-3">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${p.highlight ? "bg-gradient-to-br from-venka-blue to-venka-green text-white" : "bg-venka-blue/10 text-venka-blue"}`}><Icon className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-slate-100">{t3(p.name)}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.active ? "bg-green-500/15 text-green-300" : "bg-slate-700/40 text-slate-400"}`}>{p.active ? tx.live : tx.hidden}</span>
                    {t3(p.badge) && <span className="rounded-full bg-venka-blue/10 px-2 py-0.5 text-[10px] font-semibold text-venka-blue">{t3(p.badge)}</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400 line-clamp-2">{t3(p.tagline)}</p>
                </div>
              </div>
              <p className="mt-3 text-lg font-bold text-slate-100">
                {p.cta === "buy" && p.prices?.EUR != null ? `€${p.prices.EUR}` : tx.onRequest}
                {p.prices?.USD != null && <span className="ml-2 text-xs font-normal text-slate-400">${p.prices.USD}</span>}
                {p.prices?.AOA != null && <span className="ml-2 text-xs font-normal text-slate-400">Kz {Number(p.prices.AOA).toLocaleString()}</span>}
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-slate-400">{(p.features || []).slice(0, 4).map((f, k) => <li key={k}>• {t3(f)}</li>)}</ul>
              <div className="mt-4 flex flex-wrap items-center gap-1 border-t border-slate-800 pt-3">
                <button type="button" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800/40" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800/40" onClick={() => move(i, 1)} disabled={i === rows.length - 1}><ArrowDown className="h-4 w-4" /></button>
                <button type="button" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800/40" onClick={() => toggle(p)} title={p.active ? tx.hidden : tx.live}>{p.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button type="button" className="rounded-lg p-1.5 text-red-400 hover:bg-red-500/10" onClick={() => remove(p)}><Trash2 className="h-4 w-4" /></button>
                <Button size="sm" variant="outline" className="ml-auto rounded-full border-slate-700" onClick={() => setEdit({ ...blank(), ...p, prices: { EUR: "", USD: "", AOA: "", ...(p.prices || {}) }, features: (p.features || []).length ? p.features : [{ ...E3 }] })} data-testid={`package-edit-${p.slug}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />{tx.editShort}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="package-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? tx.edit : tx.add}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <label className="text-xs text-slate-400">{tx.code}
                  <Input className={`mt-1 font-mono ${inp}`} value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="package-slug" />
                </label>
                <div className="text-xs text-slate-400">{tx.icon}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(PACKAGE_ICONS).map(([k, I]) => (
                      <button key={k} type="button" onClick={() => setEdit({ ...edit, icon: k })} className={`grid h-9 w-9 place-items-center rounded-lg border ${edit.icon === k ? "border-venka-blue bg-venka-blue/10 text-venka-blue" : "border-slate-700 text-slate-400"}`}><I className="h-4 w-4" /></button>
                    ))}
                  </div>
                </div>
              </div>
              <div><span className="text-xs font-medium text-slate-400">{tx.name}</span><Tri value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="package-name" /></div>
              <div><span className="text-xs font-medium text-slate-400">{tx.tagline}</span><Tri multiline value={edit.tagline} onChange={(v) => setEdit({ ...edit, tagline: v })} /></div>
              <div><span className="text-xs font-medium text-slate-400">{tx.badge}</span><Tri value={edit.badge} onChange={(v) => setEdit({ ...edit, badge: v })} /></div>
              <div>
                <span className="text-xs font-medium text-slate-400">{tx.features}</span>
                <div className="mt-1 space-y-2">
                  {edit.features.map((f, k) => (
                    <div key={k} className="flex items-start gap-2">
                      <div className="flex-1"><Tri value={f} onChange={(v) => setEdit({ ...edit, features: edit.features.map((x, j) => (j === k ? v : x)) })} /></div>
                      <button type="button" className="mt-6 text-slate-500 hover:text-red-400" onClick={() => setEdit({ ...edit, features: edit.features.filter((_, j) => j !== k) })}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" className="rounded-full border-slate-700" onClick={() => setEdit({ ...edit, features: [...edit.features, { ...E3 }] })}><Plus className="h-3.5 w-3.5 mr-1" />{tx.addFeature}</Button>
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-slate-400">{tx.cta}</span>
                <div className="mt-1 grid gap-2 sm:grid-cols-3">
                  {["buy", "contact", "apply"].map((c) => (
                    <button key={c} type="button" onClick={() => setEdit({ ...edit, cta: c })} className={`rounded-xl border p-3 text-left text-sm ${edit.cta === c ? "border-venka-blue bg-venka-blue/10 text-slate-100" : "border-slate-700 text-slate-400"}`} data-testid={`package-cta-${c}`}>{tx[c]}</button>
                  ))}
                </div>
                {edit.cta === "apply" && <Input className={`mt-2 ${inp}`} placeholder="/apply/visa" value={edit.link || ""} onChange={(e) => setEdit({ ...edit, link: e.target.value })} />}
              </div>
              <div>
                <span className="text-xs font-medium text-slate-400">{tx.prices}</span>
                <div className="mt-1 grid gap-2 sm:grid-cols-3">
                  {["EUR", "USD", "AOA"].map((c) => (
                    <label key={c} className="text-[11px] font-bold text-slate-500">{c}
                      <Input type="number" min="0" step="0.01" className={`mt-1 ${inp}`} value={edit.prices?.[c] ?? ""} onChange={(e) => setEdit({ ...edit, prices: { ...edit.prices, [c]: e.target.value } })} data-testid={`package-price-${c}`} />
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">{tx.aoaHint}</p>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-200">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.highlight} onChange={(e) => setEdit({ ...edit, highlight: e.target.checked })} />{tx.highlight}</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />{tx.active}</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>{tx.cancel}</Button>
            <Button onClick={save} disabled={busy} className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid="package-save">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}{tx.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManager;
